import { Button, FieldWrapper, Label } from '@commenda/kakashi';
import {
  FieldRefSource,
  TBaseSubWorkflowStepDefinition,
  TValueProvider,
  TWorkflowDefinition,
} from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { useForm } from '../../../hooks/useZodForm';
import { ValueProviderEditor } from '../editors/ValueProviderEditor';

const schema = z.object({
  value: z.any(),
});

type TFormFields = z.infer<typeof schema>;

export const VariablesBindingForm = ({
  variableName,
  value,
  onSave,
  onCancel,
  formContentClassName,
  formFooterClassName,
  workflowDefinition,
  currentStepId,
}: {
  variableName: string;
  value: TValueProvider | null;
  onSave: (value: TValueProvider) => void;
  onCancel: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
  workflowDefinition: TWorkflowDefinition;
  currentStepId: string;
}) => {
  const { setNotification } = useNotification();

  const selectedStep = workflowDefinition.steps.find(
    (step) => step.id === currentStepId
  ) as TBaseSubWorkflowStepDefinition;
  const selectedDataSources = selectedStep?.dataSources || [];

  const { handleSubmit, watch, setValue } = useForm<TFormFields>({
    resolver: zodResolver(schema),
    defaultValues: { value: value ?? undefined },
  });

  const onSubmit = (data: TFormFields) => {
    if (!data.value) {
      setNotification({
        title: 'Error',
        message: 'Value is required',
        show: true,
        valid: false,
      });
      return;
    }
    onSave(data.value as TValueProvider);
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>Variable</Label>
          <p className="txt-small-regular text-text-primary font-mono px-3 py-2 border rounded-md bg-bg-secondary">
            {variableName}
          </p>
        </FieldWrapper>
        <FieldWrapper>
          <Label>Value</Label>
          <ValueProviderEditor
            steps={workflowDefinition.steps || []}
            dataSources={selectedDataSources}
            value={watch('value')}
            onChange={(val: TValueProvider) => setValue('value', val)}
            allowedFieldRefSources={[
              FieldRefSource.CONTEXT,
              FieldRefSource.STEP,
            ]}
          />
        </FieldWrapper>
      </div>
      <div
        className={cn('flex gap-3 justify-end border-t', formFooterClassName)}
      >
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
};
